// Module Vuex pour newVisit
// Inspiré du Reducer React NewVisit
import dexieService from '@/services/dexieService'
import { createVisit } from '@/services/mutationHelpers'
import { QUALITY_POINTS, YESNO_POINTS, RENTABLE_POINTS, YESNO_FORM_POINTS } from '@/utils/constants'
import PROGRESS_STATUS from '@/utils/progressStatus'

// Constante QUALITY_POINTS de l'ancienne app React (pour rétro-compatibilité)
export { QUALITY_POINTS, YESNO_POINTS, RENTABLE_POINTS, YESNO_FORM_POINTS }

const initialState = () => ({
  controlPoints: [],
  summary: {
    rentable: [],
    restoration: {
      comment: '',
      picturesPane: {
        open: false,
        pictures: []
      },
      inSummary: []
    }
  },
  sending: false,
  valid: false,
  messageError: null,
  messageSuccess: null,
  loading: false
})

const state = initialState()

const mutations = {
  SET_CONTROL_POINTS(state, controlPoints) {
    state.controlPoints = controlPoints
  },
  
  SET_SUMMARY(state, summary) {
    state.summary = summary
  },
  
  TOGGLE_CONTROL_POINT(state, id) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      controlPoint.checked = !controlPoint.checked
    }
  },
  
  CHECK_CONTROL_POINT(state, { id, value }) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      controlPoint.points.forEach(point => {
        point.selected = point.value === value
      })
    }
  },
  
  TOGGLE_CONTROL_POINT_COMMENT(state, id) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      const isOpening = !controlPoint.commentPane.open
      controlPoint.commentPane.open = isOpening
      // Si on ouvre le panneau de commentaire, fermer le panneau de photos
      if (isOpening) {
        controlPoint.picturesPane.open = false
      }
    }
  },
  
  CHANGE_CONTROL_POINT_COMMENT(state, { id, value }) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      controlPoint.commentPane.value = value
    }
  },
  
  TOGGLE_CONTROL_POINT_PICTURE(state, id) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      const isOpening = !controlPoint.picturesPane.open
      controlPoint.picturesPane.open = isOpening
      // Si on ouvre le panneau de photos, fermer le panneau de commentaire
      if (isOpening) {
        controlPoint.commentPane.open = false
      }
    }
  },
  
  DELETE_CONTROL_POINT_PICTURE(state, { id, pictureId }) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      controlPoint.picturesPane.pictures = controlPoint.picturesPane.pictures.filter(
        p => p.id !== pictureId
      )
    }
  },
  
  ADD_CONTROL_POINT_PICTURE(state, { id, picture }) {
    const controlPoint = state.controlPoints.find(cp => cp.id === id)
    if (controlPoint) {
      controlPoint.picturesPane.pictures.push(picture)
    }
  },
  
  SELECT_RENTABLE_POINT(state, index) {
    state.summary.rentable.forEach((item, i) => {
      item.selected = i === index
    })
  },
  
  SELECT_RESTORATION_POINT(state, index) {
    state.summary.restoration.inSummary.forEach((item, i) => {
      item.selected = i === index
    })
  },
  
  CHANGE_RESTORATION_COMMENT(state, value) {
    state.summary.restoration.comment = value
  },
  
  TOGGLE_RESTORATION_PICTURE(state) {
    state.summary.restoration.picturesPane.open = !state.summary.restoration.picturesPane.open
  },
  
  REMOVE_RESTORATION_PICTURE(state, id) {
    state.summary.restoration.picturesPane.pictures = 
      state.summary.restoration.picturesPane.pictures.filter(p => p.id !== id)
  },
  
  ADD_RESTORATION_PICTURE(state, picture) {
    state.summary.restoration.picturesPane.pictures.push(picture)
  },
  
  SET_SENDING(state, sending) {
    state.sending = sending
  },
  
  SET_VALID(state, valid) {
    state.valid = valid
  },
  
  SET_MESSAGE_ERROR(state, error) {
    state.messageError = error
  },
  
  SET_MESSAGE_SUCCESS(state, message) {
    state.messageSuccess = message
  },
  
  REMOVE_IMAGE_ERROR(state) {
    state.messageError = null
  },
  
  REMOVE_SUCCESS_MESSAGE(state) {
    state.messageSuccess = null
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  CLEAN(state) {
    Object.assign(state, initialState())
  }
}

const actions = {
  async fetchControlPoints({ commit }, _propertyId) {
    commit('SET_LOADING', true)
    try {
      
      // Récupérer les interventionPoints depuis Dexie et les trier
      const interventionPoints = await dexieService.db.table('interventionPoints')
        .toArray()
      
      // Tri selon les critères spécifiés :
      // 1er critère : position (ordre ascendant)
      // 2nd critère : id (ordre ascendant, en cas d'égalité de position)
      interventionPoints.sort((a, b) => {
        // Comparer d'abord par position
        const positionA = a.position !== undefined ? a.position : Infinity
        const positionB = b.position !== undefined ? b.position : Infinity
        
        if (positionA !== positionB) {
          return positionA - positionB
        }
        
        // Si les positions sont égales, comparer par id
        const idA = a.id
        const idB = b.id
        
        // Comparaison numérique si les ids sont des nombres
        if (typeof idA === 'number' && typeof idB === 'number') {
          return idA - idB
        }
        
        // Sinon comparaison lexicographique
        return String(idA).localeCompare(String(idB))
      })
      
      
      // Si aucun interventionPoint n'est trouvé, créer des points de contrôle par défaut
      let controlPoints = []
      
      if (interventionPoints.length === 0) {
        console.warn('Aucun point d\'intervention trouvé dans Dexie, utilisation de points par défaut')
      } else {
        // Transformer les interventionPoints en controlPoints pour le formulaire
        controlPoints = interventionPoints.map((point, index) => {
          // Déterminer les points en fonction du answerType
          let availablePoints
          if (point.answerType === 'answer_yesno') {
            availablePoints = YESNO_POINTS
          } else {
            // Par défaut, utiliser QUALITY_POINTS (pour answer_state ou autres)
            availablePoints = QUALITY_POINTS
          }
          
          return {
            id: point.id || String(index + 1),
            category: point.category || point.section?.name || 'Catégorie',
            title: point.title || point.label || point.name || 'Point de contrôle',
            points: point.values ? point.values.map((val, i) => ({
              label: val.label || val,
              value: val.value !== undefined ? val.value : i + 1,
              selected: false
            })) : availablePoints.map(qp => ({ ...qp })),
            checked: false,
            commentPane: { open: false, value: '' },
            picturesPane: { open: false, pictures: [] }
          }
        })
      }
      
      
      commit('SET_CONTROL_POINTS', controlPoints)
      
      // Initialiser le résumé avec RENTABLE_POINTS et YESNO_FORM_POINTS depuis constants.js
      const summary = {
        rentable: RENTABLE_POINTS.map(point => ({ ...point, selected: false })),
        restoration: {
          comment: '',
          picturesPane: { open: false, pictures: [] },
          inSummary: YESNO_FORM_POINTS.map(point => ({ ...point, selected: false }))
        }
      }
      
      commit('SET_SUMMARY', summary)
      
      
    } catch (error) {
      const errorMessage = 'Erreur lors du chargement des points de contrôle'
      commit('SET_MESSAGE_ERROR', errorMessage)
      console.error('Error fetching control points:', error)
      
      // En cas d'erreur, initialiser avec des valeurs par défaut pour permettre l'affichage
      commit('SET_CONTROL_POINTS', [])
      commit('SET_SUMMARY', {
        rentable: RENTABLE_POINTS.map(point => ({ ...point, selected: false })),
        restoration: {
          comment: '',
          picturesPane: { open: false, pictures: [] },
          inSummary: YESNO_FORM_POINTS.map(point => ({ ...point, selected: false }))
        }
      })
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  toggleControlPoint({ commit }, id) {
    commit('TOGGLE_CONTROL_POINT', id)
  },
  
  checkControlPoint({ commit }, { id, value }) {
    commit('CHECK_CONTROL_POINT', { id, value })
  },
  
  toggleControlPointComment({ commit }, id) {
    commit('TOGGLE_CONTROL_POINT_COMMENT', id)
  },
  
  changeControlPointComment({ commit }, { id, value }) {
    commit('CHANGE_CONTROL_POINT_COMMENT', { id, value })
  },
  
  toggleControlPointPicture({ commit }, id) {
    commit('TOGGLE_CONTROL_POINT_PICTURE', id)
  },
  
  deleteControlPointPicture({ commit }, { id, pictureId }) {
    commit('DELETE_CONTROL_POINT_PICTURE', { id, pictureId })
  },
  
  addControlPointPicture({ commit }, { id, picture }) {
    commit('ADD_CONTROL_POINT_PICTURE', { id, picture })
  },
  
  selectRentablePoint({ commit }, index) {
    commit('SELECT_RENTABLE_POINT', index)
  },
  
  selectRestorationPoint({ commit }, index) {
    commit('SELECT_RESTORATION_POINT', index)
  },
  
  changeRestorationComment({ commit }, value) {
    commit('CHANGE_RESTORATION_COMMENT', value)
  },
  
  toggleRestorationPicture({ commit }) {
    commit('TOGGLE_RESTORATION_PICTURE')
  },
  
  removeRestorationPicture({ commit }, id) {
    commit('REMOVE_RESTORATION_PICTURE', id)
  },
  
  addRestorationPicture({ commit }, picture) {
    commit('ADD_RESTORATION_PICTURE', picture)
  },
  
  removeImageError({ commit }) {
    commit('REMOVE_IMAGE_ERROR')
  },
  
  removeSuccessMessage({ commit }) {
    commit('REMOVE_SUCCESS_MESSAGE')
  },
  
  async send({ commit, state }, { folderId }) {
    commit('SET_SENDING', true)
    commit('SET_MESSAGE_ERROR', null)
    
    try {
      // Récupérer l'utilisateur connecté depuis dexieService (comme dans authService)
      const currentUser = await dexieService.getUser()
      
      // Convertir les images en format sérialisable
      const serializeControlPoints = (controlPoints) => {
        return controlPoints.map(cp => ({
          ...cp,
          picturesPane: {
            ...cp.picturesPane,
            pictures: cp.picturesPane.pictures.map(pic => ({
              id: pic.id,
              // Conserver uniquement le src (data URL) qui est sérialisable
              src: pic.file?.src || pic.src,
              name: pic.file?.name || pic.name
            }))
          }
        }))
      }

      const serializeSummary = (summary) => {
        return {
          ...summary,
          restoration: {
            ...summary.restoration,
            picturesPane: {
              ...summary.restoration.picturesPane,
              pictures: summary.restoration.picturesPane.pictures.map(pic => ({
                id: pic.id,
                src: pic.file?.src || pic.src,
                name: pic.file?.name || pic.name
              }))
            }
          }
        }
      }

      // Générer un ID temporaire unique
      const tempId = `temp_visit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      
      // Créer les contrôles à partir des points de contrôle AVANT de créer la visite
      const controls = state.controlPoints
        .filter(cp => cp.points.some(p => p.selected))
        .map(cp => {
          const selectedPoint = cp.points.find(p => p.selected)
          return {
            id: cp.id,
            interventionPoint: {
              id: cp.id,
              position: cp.category
            },
            category: cp.category,
            title: cp.title,
            label: cp.title,
            value: selectedPoint.value,
            answer: selectedPoint.value,
            comment: cp.commentPane.value || null,
            commentPane: {
              open: false,
              value: cp.commentPane.value || ''
            },
            picturesPane: {
              open: false,
              pictures: cp.picturesPane.pictures.map(pic => ({
                id: pic.id,
                src: pic.file?.src || pic.src,
                name: pic.file?.name || pic.name
              }))
            },
            points: cp.points,
            checked: cp.checked,
            actionStatus: cp.checked ? PROGRESS_STATUS.TODO : null,
            insertedAt: new Date().toISOString()
          }
        })
      
      // Préparer les données de la visite avec images sérialisées ET les controls
      const visitData = {
        id: tempId,
        propertyId: folderId,
        property: { id: folderId },
        controlPoints: serializeControlPoints(state.controlPoints),
        controls: controls, // Ajouter les controls dans visitData
        summary: serializeSummary(state.summary),
        timestamp: new Date().toISOString(),
        status: 'pending',
        type: 'etat_des_lieux',
        date: new Date().toISOString(),
        // Ajouter les informations de l'utilisateur connecté comme auteur
        author: currentUser ? {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          fullName: currentUser.fullName || `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
        } : null,
        technicalManager: currentUser ? {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          fullName: currentUser.fullName || `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
        } : null
      }
      
      // Utiliser createVisit de mutationHelpers pour gérer l'API et l'enregistrement dans mutations
      const result = await createVisit(visitData)
      
      // Si le résultat n'est pas un succès complet (échec même du stockage local)
      if (!result.success) {
        const errorMsg = result.error || 'Erreur lors de la création de la visite'
        commit('SET_MESSAGE_ERROR', errorMsg)
        return { success: false, error: errorMsg }
      }
      
      const visitId = result.result?.id || tempId
      
      // Créer les contrôles pour Dexie (format différent de ceux dans visitData)
      const controlsForDexie = state.controlPoints
        .filter(cp => cp.points.some(p => p.selected))
        .map(cp => {
          const selectedPoint = cp.points.find(p => p.selected)
          return {
            id: `control-${cp.id}-${Date.now()}`,
            visit: {
              id: visitId,
              property: {
                id: folderId
              }
            },
            interventionPoint: {
              id: cp.id,
              position: cp.category
            },
            value: selectedPoint.value,
            comment: cp.commentPane.value || null,
            // Convertir les images en format sérialisable
            pictures: cp.picturesPane.pictures.map(pic => ({
              id: pic.id,
              src: pic.file?.src || pic.src,
              name: pic.file?.name || pic.name
            })),
            actionStatus: cp.checked ? PROGRESS_STATUS.TODO : null,
            insertedAt: new Date().toISOString()
          }
        })
      
      // Sauvegarder les contrôles dans Dexie
      if (controlsForDexie.length > 0) {
        await dexieService.saveControls(controlsForDexie)
      }
      
      // Succès : pas d'erreur à afficher
      // Si synced=false, la visite sera synchronisée plus tard automatiquement
      commit('SET_MESSAGE_ERROR', null)
      
      // Si non synchronisée avec l'API, afficher un message d'information
      if (!result.synced) {
        commit('SET_MESSAGE_SUCCESS', 'Visite enregistrée localement. Elle sera synchronisée automatiquement lors de la prochaine connexion.')
      }
      
      return { 
        success: true, 
        visitId: visitId,
        synced: result.synced
      }
      
    } catch (error) {
      console.error('Error sending visit:', error)
      const errorMsg = 'Erreur lors de l\'enregistrement de la visite'
      commit('SET_MESSAGE_ERROR', errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      commit('SET_SENDING', false)
    }
  },
  
  clean({ commit }) {
    commit('CLEAN')
  }
}

const getters = {
  valid: (state) => {
    // Vérifier que tous les points de contrôle ont au moins une option sélectionnée
    const allPointsSelected = state.controlPoints.every(cp => 
      cp.points.some(point => point.selected)
    )
    
    // Vérifier que le résumé est rempli
    const rentableSelected = state.summary.rentable.some(r => r.selected)
    const restorationSelected = state.summary.restoration.inSummary.some(r => r.selected)
    const commentFilled = state.summary.restoration.comment.trim().length > 0
    
    return allPointsSelected && rentableSelected && restorationSelected && commentFilled
  },
  
  controlPoints: (state) => state.controlPoints,
  summary: (state) => state.summary,
  sending: (state) => state.sending,
  messageError: (state) => state.messageError,
  messageSuccess: (state) => state.messageSuccess,
  loading: (state) => state.loading
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
